import { useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ActivityHeatmap } from '@/components/viz/ActivityHeatmap';
import { ConstellationNetwork } from '@/components/viz/ConstellationNetwork';
import { FlowRiver } from '@/components/viz/FlowRiver';
import { RadialClock } from '@/components/viz/RadialClock';
import { Range, useTriggerStream } from '@/hooks/useTriggerStream';
import { Seo } from '@/components/Seo';

type EmbedView = 'constellation' | 'heatmap' | 'clock' | 'river';

const VIEWS: EmbedView[] = ['constellation', 'heatmap', 'clock', 'river'];

const VIEW_META: Record<EmbedView, { title: string; description: string }> = {
  constellation: {
    title: 'Constellation Visualization · Perpetual Plexus',
    description: 'Live constellation view of the Perpetual Plexus network: each device is a star and pulses travel between them as triggers fire.',
  },
  heatmap: {
    title: 'Activity Heatmap · Perpetual Plexus',
    description: 'Seven days of Perpetual Plexus motion activity folded into a day-by-hour heatmap, with darker marks for busier hours.',
  },
  clock: {
    title: 'Radial Clock Visualization · Perpetual Plexus',
    description: 'A radial clock of Perpetual Plexus activity, mapping every motion trigger to its hour of the day around a 24-hour dial.',
  },
  river: {
    title: 'Flow River Visualization · Perpetual Plexus',
    description: 'Stacked flow-river streams of Perpetual Plexus activity, one coloured band per device, showing traffic over the selected time window.',
  },
};

function isView(value: string | undefined): value is EmbedView {
  return Boolean(value && VIEWS.includes(value as EmbedView));
}

function isRange(value: string | null): value is Range {
  return value === '1h' || value === '24h' || value === '7d' || value === '30d' || value === '1y' || value === '10y';
}

export default function EmbedVisualization() {
  const { view } = useParams();
  const [searchParams] = useSearchParams();
  const requestedRange = searchParams.get('range');
  const range: Range = isRange(requestedRange) ? requestedRange : '7d';
  const dark = false;
  const { triggers, devices, lastTrigger, loading } = useTriggerStream(range, true, true);

  const chart = useMemo(() => {
    if (!isView(view)) return null;
    if (view === 'constellation') {
      return <ConstellationNetwork devices={devices} triggers={triggers} lastTrigger={lastTrigger} dark={dark} />;
    }
    if (view === 'heatmap') return <ActivityHeatmap triggers={triggers} dark={dark} />;
    if (view === 'clock') return <RadialClock triggers={triggers} dark={dark} />;
    return <FlowRiver triggers={triggers} devices={devices} range={range} dark={dark} />;
  }, [view, devices, triggers, lastTrigger, dark, range]);

  if (!isView(view)) return <Navigate to="/embed/constellation" replace />;

  const meta = VIEW_META[view];

  return (
    <main
      className="min-h-screen w-full overflow-hidden flex items-center justify-center p-3 md:p-5 bg-[var(--viz-embed-light)] text-[var(--viz-embed-dark)]"
      aria-label={`Perpetual Plexus ${view} activity visualization`}
    >
      <Seo title={meta.title} description={meta.description} path={`/embed/${view}`} />
      {loading ? (
        <div className="font-mono text-xs uppercase tracking-widest opacity-50">Loading live activity…</div>
      ) : (
        <div className="w-full max-h-screen">{chart}</div>
      )}
    </main>
  );
}